<script lang="ts">
	import type { CartState } from '../../handlers/cart_state.svelte';

	import LucideIcon from '$lib/utils/components/LucideIcon.svelte';
	import { Button } from '$lib/components/ui/button';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';

	import { Loc } from '$lib/tools/locale.svelte';
	import { utils } from '$lib/utils/utils.svelte';

	let { cart_state }: { cart_state: CartState } = $props();

	let phone = $state('');
	let name = $state('');
	let loading = $state(false);
	let error_msg = $state('');

	async function search() {
		if (phone.length < 10) return;
		loading = true;
		try {
			const buyers = await cart_state.db_fns.does_phone_number_exists_fn(phone);
			if (buyers && buyers.length > 0) cart_state.variables.buyer = buyers[0];
			else cart_state.variables.buyer = { name: '', phone_number: phone, is_imported: false, is_undefined: true };
		} finally {
			loading = false;
		}
	}

	async function parse_and_search() {
		if (loading) return;
		error_msg = '';

		// If there's already exactly 10 valid digits typed, just search.
		if (phone.length === 10 && /^09\d{8}$/.test(phone)) {
			return search();
		}

		try {
			const pasted_text = await navigator.clipboard.readText();
			if (!pasted_text) {
				error_msg = 'clipboard_empty';
				return;
			}
			const result = utils.numbers.parse_libyan_phone(pasted_text);

			if (result.phone) {
				phone = result.phone;
				search();
			} else if (result.error) {
				error_msg = result.error;
			}
		} catch (err) {
			error_msg = 'clipboard_access_failed';
		}
	}

	function reset() {
		cart_state.variables.buyer = undefined;
		phone = '';
		name = '';
	}
	let phone_number_input = $state<HTMLInputElement | null>(null);
</script>

<div class="w-full">
	{#if !cart_state.variables.buyer}
		<div class="space-y-2">
			<div class="flex items-center justify-between">
				<Label for="phone-input" class="text-sm font-medium">{Loc('phone_number')}</Label>
				{#if error_msg}
					<span class="text-xs text-destructive animate-in fade-in slide-in-from-top-1">{Loc(error_msg)}</span>
				{/if}
			</div>

			<!-- <Label>{Loc('phone_number')}</Label> -->
			<div class="flex w-full items-center space-x-2 rtl:space-x-reverse group">
				<div class="relative flex-1">
					<Input
						id="phone-input"
						dir="ltr"
						type="tel"
						maxlength={10}
						placeholder="09XXXXXXXX"
						class="font-mono rounded-s-none focus-visible:ring-1"
						bind:ref={phone_number_input}
						bind:value={phone}
						onkeypress={(e) => {
							const input_value = e.key;
							if (!input_value || !phone_number_input) return;

							const valid_phone_number_char = /^[0-9]$/;
							const selected_text_length =
								(phone_number_input.selectionEnd ?? 0) - (phone_number_input.selectionStart ?? 0);
							const newInputLength = phone_number_input.value?.length - selected_text_length + 1;

							if (!valid_phone_number_char.test(input_value) || newInputLength > 10) {
								e.preventDefault();
							}
							error_msg = '';
						}}
						onpaste={(e) => {
							const pasted_text = e.clipboardData?.getData('text');
							const pasted_length = pasted_text?.length;
							if (!pasted_length || !phone_number_input) return;
							const selected_text_length =
								(phone_number_input.selectionEnd ?? 0) - (phone_number_input.selectionStart ?? 0);
							if (pasted_length + (phone_number_input.value?.length ?? 0) - selected_text_length > 10) {
								e.preventDefault();
							}
							error_msg = '';
						}}
						oninput={() => {
							if (phone.length === 10) search();
						}}
					/>
					{#if phone.length === 10 && !loading}
						<div class="absolute inset-y-0 right-2 rtl:left-2 rtl:right-auto flex items-center">
							<span class="flex h-2 w-2 rounded-full bg-success animate-pulse"></span>
						</div>
					{/if}
				</div>

				<Button
					type="button"
					size="icon"
					disabled={loading}
					class="rounded-s-none group-focus-within:border-ring group-focus-within:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive group-focus-within:ring-1 ring-offset-background"
					onclick={parse_and_search}
				>
					{#if loading}
						<LucideIcon name="Loader2" class="animate-spin" size="16" />
					{:else if phone.length === 10 && !loading}
						<div use:utils.actions.tooltip={{ content: Loc('search'), hideOnClick: true }}>
							<LucideIcon name="Search" size="16" />
						</div>
					{:else}
						<div use:utils.actions.tooltip={{ content: Loc('copy_from_clipboard'), hideOnClick: true }}>
							<LucideIcon name="ClipboardPaste" size="16" />
						</div>
					{/if}
				</Button>
			</div>
		</div>
	{:else}
		<div
			class="relative flex items-center gap-4 rounded-lg border border-border bg-card p-4 shadow-sm transition-all hover:border-primary/50"
		>
			<div class="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
				<LucideIcon name="User" size="20" />
			</div>

			<div class="flex-1 space-y-1">
				<p class="text-xs font-medium text-muted-foreground uppercase">{Loc('phone_number')}</p>
				<p class="font-mono font-bold text-lg leading-none">{cart_state.variables.buyer.phone_number}</p>

				<div class="pt-1">
					{#if cart_state.variables.buyer.is_imported}
						<p class="text-sm font-medium">{cart_state.variables.buyer.name}</p>
					{:else}
						<Input
							bind:value={cart_state.variables.buyer.name}
							placeholder={Loc('name|buyer,the')}
							class="h-8 text-sm mt-1"
						/>
					{/if}
				</div>
			</div>

			<Button
				variant="ghost"
				size="icon"
				class="absolute top-2 right-2 rtl:left-2 rtl:right-auto h-8 w-8 text-muted-foreground hover:text-destructive"
				onclick={reset}
			>
				<LucideIcon name="X" size="16" />
			</Button>
		</div>
	{/if}
</div>
